// Player inventory screen (UI-3/4): a profile band (vertical armor column +
// offhand beside a silhouette doll — "what's equipped"), a labeled 2×2 crafting
// grid with output, and the labeled storage/hotbar panel. Opened with E. Reuses
// the generic window + shared craft grid (crafting.ts). Client-only (ENG-1).
import { INV } from '../../../sim/game/inventory';
import { openWindow, returnToInventory, type SlotRef } from './container';
import { makeCraftGrid, makeRecipeBookFooter, playerRefs, playerRows, playerQuickTargets } from './crafting';
import { h } from '../h';
import type { Game } from '../../boot';

const ARMOR_LABELS = ['Helmet', 'Chestplate', 'Leggings', 'Boots'] as const;

/** A plain silhouette standing in for a real skinned player model (the game
 *  has no rigged/skinned character mesh yet — see remoteplayers.ts, a tinted
 *  box+head avatar). Just enough shape to read as "this is your character"
 *  next to the armor column, without inventing a new 3D-preview pipeline. */
function buildPlayerDoll(): HTMLElement {
  return h('div', { class: 'bb-doll' },
    h('div', { class: 'bb-doll-head' }),
    h('div', { class: 'bb-doll-torso' },
      h('div', { class: 'bb-doll-arm bb-doll-arm-l' }),
      h('div', { class: 'bb-doll-arm bb-doll-arm-r' })),
    h('div', { class: 'bb-doll-legs' }));
}

export function openInventory(game: Game): void {
  const pinv = game.playerState.inventory;
  const pr = playerRefs(pinv);
  const g = makeCraftGrid(game, 2);
  const gridSet = new Set<object>([g.craft, g.output]);

  const armor: SlotRef[] = ARMOR_LABELS.map((label, i) => ({
    slots: pinv,
    index: INV.ARMOR_START + i,
    testId: `armor-${label.toLowerCase()}`,
    title: label,
  }));
  const offhand: SlotRef = { slots: pinv, index: INV.OFFHAND, testId: 'offhand', title: 'Offhand' };

  // Profile band: armor in a vertical column (helmet→boots) + offhand below,
  // beside the doll — matches how Minecraft's own inventory reads at a glance.
  const profileRows: (SlotRef | null)[][] = [[armor[0]!], [armor[1]!], [armor[2]!], [armor[3]!], [offhand]];

  const layout: (SlotRef | null)[][] = [
    ...profileRows,
    [null, ...g.gridRefs.slice(0, 2), null, g.outputRef],
    [null, ...g.gridRefs.slice(2, 4)],
    [null],
    ...playerRows(pr),
  ];
  const craftRow = profileRows.length; // first row after the profile band
  const invRow = craftRow + 3; // + 2 craft rows + 1 gap row

  const { footer, onOpen, refresh } = makeRecipeBookFooter(game, g, 2);

  openWindow(game, {
    title: 'Inventory',
    layout,
    header: buildPlayerDoll(),
    headerRows: profileRows.length,
    rowLabels: { [craftRow]: 'Crafting', [invRow]: 'Inventory' },
    quickTargets: (ref) => playerQuickTargets(ref, pr, gridSet),
    onChange: () => { g.recompute(); refresh(); },
    onOutputTaken: g.consume,
    onClose: (cursor) => returnToInventory(game, [cursor, ...g.craft.slots]),
    onOpen,
    footer,
  });
}
